import { useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from '@microsoft/signalr';
import { BASE_URL, getAccessToken } from './api';

export type InboxEvent =
  | { type: 'message.received'; conversationId: number }
  | { type: 'message.status'; conversationId: number; messageId: number; status: number }
  | { type: 'conversation.changed'; conversationId: number }
  /**
   * Something worth interrupting somebody about.
   *
   * <b>Unlike the three above, this one carries its words.</b> Those are
   * cache-coherence signals: an id is enough, because the screen refetches through
   * the query a page load uses and so a push can never disagree with a reload. That
   * is the wrong trade here twice over — a popup has to be drawn on whatever screen
   * the agent is actually on, where there is no inbox cache to refetch into, and a
   * popup that waits for an HTTP round trip appears after they have looked away.
   *
   * `kind` is one of NOTIFICATION_KIND. `groupKey` is stable for the fact being
   * announced, so ten messages in one chat replace one popup rather than stacking
   * ten. `conversationId` is what lets the browser stay quiet about the conversation
   * already on screen.
   */
  | {
      type: 'notification';
      kind: string;
      groupKey: string;
      title: string;
      body: string;
      url?: string | null;
      conversationId?: number | null;
      at: string;
    }
  /**
   * This agent changed their notification settings somewhere else.
   *
   * The surfaces are applied in the browser from a cached copy of the preferences,
   * so without this a second tab keeps playing a sound its user has just switched
   * off — and "I turned it off and it still does it" is how a settings screen loses
   * its user for good.
   */
  | { type: 'notification.preferences' };

/**
 * The three events that exist to keep a cache honest.
 *
 * Separated from the union because the two new members are not that: a notification
 * carries words and is consumed once, by the shell, to make a noise. A screen
 * subscribing for cache updates must not have to write a branch rejecting them, and
 * naming the set is what stops the compiler asking it to.
 */
export type InboxCacheEvent = Extract<
  InboxEvent,
  { type: 'message.received' | 'message.status' | 'conversation.changed' }
>;

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

/**
 * Live inbox updates.
 *
 * accessTokenFactory is read on every connect and reconnect, so a token refreshed
 * while the socket was down is picked up automatically — a connection does not
 * outlive its credential, and a reconnect never replays a stale one.
 *
 * Events carry ids only. The handler refetches through the same queries a page load
 * uses, so a live update and a refresh can never render different things.
 *
 * `onResumed` is the half that was missing, and it is the whole of the bug that a
 * busy inbox showed nothing until somebody refreshed. <b>SignalR does not replay.</b>
 * Anything raised while the socket was down — a laptop asleep, a tab throttled by
 * the browser, a wifi change, an API deploy, an app-pool recycle — is gone, and the
 * connection then comes back and sits there looking perfectly healthy. Every agent
 * who had been disconnected for ten seconds was reading a frozen inbox with nothing
 * on screen to say so.
 *
 * So a resumed connection is treated as a page load: refetch, and the gap is closed.
 * It deliberately does not fire on the first connection of a mount — the queries
 * have just run — only on a connection that replaces one that went away.
 */
export function useInboxHub(
  enabled: boolean,
  onEvent: (event: InboxEvent) => void,
  onResumed?: () => void,
): ConnectionState {
  const [state, setState] = useState<ConnectionState>('disconnected');
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const resumed = useRef(onResumed);
  resumed.current = onResumed;

  useEffect(() => {
    if (!enabled) {
      setState('disconnected');
      return;
    }

    // Backs off 0s, 2s, 10s, then every 30s, and never gives up. The array form
    // of withAutomaticReconnect stops after its last entry (it appends a final
    // "null" = close), so a laptop asleep for a minute came back to a hub that had
    // quietly closed for good — no new messages until a reload, and nothing on
    // screen to say so. A policy object retries forever.
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`${BASE_URL}/hubs/inbox`, { accessTokenFactory: () => getAccessToken() ?? '' })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
          previousRetryCount === 0 ? 0 : previousRetryCount === 1 ? 2000 : previousRetryCount === 2 ? 10_000 : 30_000,
      })
      .configureLogging(LogLevel.Warning)
      .build();

    // The initial start can fail too (API restarting as the page loads), and
    // automatic reconnect only covers a connection that was once up.
    let stopped = false;
    let everConnected = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const start = (attempt: number) => {
      if (stopped) return;
      setState(attempt === 0 ? 'connecting' : 'reconnecting');
      connection.start()
        .then(() => {
          if (stopped) return;
          setState('connected');
          // A cold restart after onclose, or a first attempt that only succeeded on
          // retry: either way time passed with nothing listening.
          if (everConnected || attempt > 0) resumed.current?.();
          everConnected = true;
        })
        .catch(() => {
          if (stopped) return;
          setState('disconnected');
          retry = setTimeout(() => start(attempt + 1), Math.min(30_000, 2000 * 2 ** attempt));
        });
    };

    connection.on('message.received', (p: { conversationId: number }) =>
      handler.current({ type: 'message.received', ...p }));
    connection.on('message.status', (p: { conversationId: number; messageId: number; status: number }) =>
      handler.current({ type: 'message.status', ...p }));
    connection.on('conversation.changed', (p: { conversationId: number }) =>
      handler.current({ type: 'conversation.changed', ...p }));
    connection.on('notification', (p: Omit<Extract<InboxEvent, { type: 'notification' }>, 'type'>) =>
      handler.current({ type: 'notification', ...p }));
    connection.on('notification.preferences', () =>
      handler.current({ type: 'notification.preferences' }));

    connection.onreconnecting(() => setState('reconnecting'));
    connection.onreconnected(() => { setState('connected'); resumed.current?.(); });
    // onclose fires when the reconnect policy is exhausted or the server ends the
    // connection outright; either way, start again from cold.
    connection.onclose(() => {
      setState('disconnected');
      if (!stopped) retry = setTimeout(() => start(1), 2000);
    });

    start(0);

    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      if (connection.state !== HubConnectionState.Disconnected) void connection.stop();
    };
  }, [enabled]);

  return state;
}
